import { useState, useEffect } from "react";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface PhoneFieldProps {
  form: any;
}

const COUNTRY_CODES = [
  { code: "+1", country: "USA/Canada", flag: "🇺🇸", minLength: 10, maxLength: 10 },
  { code: "+44", country: "UK", flag: "🇬🇧", minLength: 10, maxLength: 15 },
  { code: "+91", country: "India", flag: "🇮🇳", minLength: 10, maxLength: 10 },
  { code: "+86", country: "China", flag: "🇨🇳", minLength: 11, maxLength: 12 },
  { code: "+81", country: "Japan", flag: "🇯🇵", minLength: 10, maxLength: 10 },
  { code: "+49", country: "Germany", flag: "🇩🇪", minLength: 10, maxLength: 11 },
  { code: "+33", country: "France", flag: "🇫🇷", minLength: 9, maxLength: 9 },
  { code: "+61", country: "Australia", flag: "🇦🇺", minLength: 9, maxLength: 9 },
  { code: "+55", country: "Brazil", flag: "🇧🇷", minLength: 10, maxLength: 11 },
  { code: "+52", country: "Mexico", flag: "🇲🇽", minLength: 10, maxLength: 10 },
];

const PhoneField = ({ form }: PhoneFieldProps) => {
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const phoneValue = form.watch("phone");

  // Sync local state with form value when phone is loaded from saved data
  useEffect(() => {
    if (phoneValue && phoneValue.startsWith("+")) {
      // Parse the phone value to extract country code and number
      const matchedCountry = COUNTRY_CODES.find(c => phoneValue.startsWith(c.code));
      if (matchedCountry) {
        setCountryCode(matchedCountry.code);
        setPhoneNumber(phoneValue.slice(matchedCountry.code.length));
      }
    }
  }, [phoneValue]);

  const validatePhoneNumber = (code: string, number: string) => {
    if (!number) {
      setPhoneError("");
      return false;
    }

    const country = COUNTRY_CODES.find(c => c.code === code);
    if (!country) {
      setPhoneError("Please select a country code");
      return false;
    }

    const length = number.length;
    
    if (length < country.minLength || length > country.maxLength) {
      if (country.minLength === country.maxLength) {
        setPhoneError(`${country.country} numbers must be ${country.minLength} digits`);
      } else {
        setPhoneError(`${country.country} numbers must be ${country.minLength}-${country.maxLength} digits`);
      }
      return false;
    }

    const fullPhone = `${code}${number}`;
    const e164Regex = /^\+[1-9]\d{9,14}$/;
    if (!e164Regex.test(fullPhone)) {
      setPhoneError("Please select country code and enter phone number");
      return false;
    }

    setPhoneError("");
    return true;
  };


  return (
    <FormField
      control={form.control}
      name="phone"
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel className={fieldState.error || phoneError ? "text-destructive" : ""}>Phone Number</FormLabel>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Phone number for booking confirmations and event updates</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger className="w-full sm:w-[140px] bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_CODES.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1">
              <FormControl>
                <Input
                  placeholder="2025551234"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setPhoneNumber(value);
                    field.onChange(`${countryCode}${value}`);
                    validatePhoneNumber(countryCode, value);
                  }}
                  className="flex-1 bg-background/50"
                />
              </FormControl>
              {phoneError && (
                <p className="text-sm text-destructive mt-1">{phoneError}</p>
              )}
            </div>
          </div>
        </FormItem>
      )}
    />
  );
};

export default PhoneField;
